import { Dropdown, Option, OptionOnSelectData, SelectionEvents } from '@fluentui/react-components'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { Button } from '../Button'
import { ErrorComponent } from '../ErrorComponent'
import { AppAdminCard } from '../FluentCard/AppAdminCard'
import { Loading } from '../Loading'

type MessagesApplicationsApplication = RestSchemas['Messages.Applications.Application']
type MessagesApplicationsUser = RestSchemas['Messages.Applications.User']

type MaintainAppAdminProps = {
  selectedApp: MessagesApplicationsApplication
  onSubmit?: () => void
}

export const MaintainAppAdmins = (props: MaintainAppAdminProps) => {
  const queryClient = useQueryClient()

  const {
    getApplicationsApiListAllAdmins,
    getApplicationsApiListApplicationAdmins,
    putApplicationsApiAddApplicationAdmin,
    deleteApplicationsApiRemoveApplicationAdmin,
  } = createRestClient()

  const {
    data: allAdmins,
    // isPending: isAllAdminsPending,
    // isError: isAllAdminsError,
  } = getApplicationsApiListAllAdmins.useQuery()

  const {
    data: currentAppAdmins,
    isPending: isCurrentAppAdminsPending,
    isError: isCurrentAppAdminsError,
  } = getApplicationsApiListApplicationAdmins.useQuery({
    query: { applicationId: props.selectedApp.applicationId },
  })

  const {
    mutateAsync: addApplicationAdmin,
    // isPending: isAddAdminPending,
    // isError: isAddAdminError,
  } = putApplicationsApiAddApplicationAdmin.useMutation()
  const {
    mutate: deleteApplicationAdmin,
    // isPending: isDeleteAdminPending,
    // isError: isDeleteAdminError,
  } = deleteApplicationsApiRemoveApplicationAdmin.useMutation()

  const [selectedOptions, setSelectedOptions] = useState<MessagesApplicationsUser[]>([])

  const invalidateCurrentAppAdmins = () => {
    getApplicationsApiListApplicationAdmins.invalidateQueries(
      {
        parameters: {
          query: {
            applicationId: props.selectedApp.applicationId,
          },
        },
      },
      queryClient
    )
  }

  const onOptionSelect = (_event: SelectionEvents, data: OptionOnSelectData) => {
    const _selectedOptions = allAdmins?.filter((user) => data.selectedOptions.includes(user.id.toString()))
    setSelectedOptions(_selectedOptions ?? [])
  }

  const onAddSubmit = () => {
    if (selectedOptions.length === 0) {
      return
    }
    const promises = selectedOptions.map((admin) =>
      addApplicationAdmin(
        {
          query: { applicationId: props.selectedApp.applicationId, adminId: admin.id },
        },
        {
          onSuccess() {
            toast.success(`Added admin`)
          },
          onError(error) {
            toast.error(`Error adding admin: ${error}`)
          },
        }
      )
    )
    Promise.all(promises).finally(() => {
      setSelectedOptions([])
      invalidateCurrentAppAdmins()
    })
  }

  const onDelete = (adminId: number) => {
    deleteApplicationAdmin(
      {
        query: { applicationId: props.selectedApp.applicationId, adminId },
      },
      {
        onSuccess() {
          toast.success(`Deleted admin`)
          invalidateCurrentAppAdmins()
        },
        onError(error) {
          toast.error(`Error deleting admin: ${error}`)
        },
      }
    )
  }

  return (
    <div className="flex flex-col gap-5 pb-5">
      <Dropdown
        aria-labelledby={'add-user-as-app-admin'}
        multiselect={true}
        placeholder="Add app admins"
        value={selectedOptions.map((option) => `${option.givenName} ${option.familyName}`).join(', ')}
        selectedOptions={selectedOptions.map((option) => option.id.toString())}
        onOptionSelect={onOptionSelect}
      >
        <div className="max-h-[600px] overflow-y-scroll">
          {(allAdmins ?? []).map((option) => (
            <Option
              key={option.id}
              disabled={(currentAppAdmins ?? []).map((admin) => admin.id).includes(option.id)}
              value={option.id.toString()}
            >
              {`${option.givenName} ${option.familyName}`}
            </Option>
          ))}
        </div>
      </Dropdown>
      <div className="flex flex-row justify-end">
        <Button disabled={selectedOptions.length === 0} text="Add admins" onClick={onAddSubmit} />
      </div>
      {isCurrentAppAdminsPending ? (
        <Loading />
      ) : isCurrentAppAdminsError ? (
        <ErrorComponent />
      ) : (
        currentAppAdmins.map((admin) => (
          <AppAdminCard key={admin.id} admin={admin} onDelete={() => onDelete(admin.id)} />
        ))
      )}
    </div>
  )
}
